import { useState, useEffect, useRef } from "react";
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, where } from "firebase/firestore";
import { db } from "../../firebase";
import toast, { Toaster } from "react-hot-toast";
import { Award, Upload, Trash2, Search, X, Users, FileText } from "lucide-react";

const CLOUD_NAME    = "dan0bapfg";
const UPLOAD_PRESET = "htst_event";

async function uploadToCloudinary(file, folder) {
  const formData = new FormData();
  formData.append("file",          file);
  formData.append("upload_preset", UPLOAD_PRESET);
  formData.append("folder",        `htst_certificates/${folder}`);
  const res  = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method:"POST", body:formData
  });
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  return data.secure_url;
}

export default function CertificateManager() {
  const [members,      setMembers]      = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [uploading,    setUploading]    = useState(false);
  const [search,       setSearch]       = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [file,         setFile]         = useState(null);
  const [preview,      setPreview]      = useState(null);
  const [filterMember, setFilterMember] = useState("all");
  const [form,         setForm]         = useState({ title:"", eventName:"", issuedDate:"" });
  const fileRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const mSnap = await getDocs(query(collection(db,"users"), orderBy("name")));
      const cSnap = await getDocs(query(collection(db,"documents"), orderBy("issuedDate","desc")));
      setMembers(mSnap.docs.map(d=>({id:d.id,...d.data()})));
      setCertificates(cSnap.docs.map(d=>({id:d.id,...d.data()})));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  function handleFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Max file size is 5MB");
    setFile(f);
    if (f.type.startsWith("image/")) setPreview(URL.createObjectURL(f));
    else setPreview(null);
  }

  async function handleUpload() {
    if (!selectedMember)  return toast.error("Please select a member");
    if (!file)            return toast.error("Please select a file");
    if (!form.title)      return toast.error("Please enter a certificate title");
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file, `${selectedMember.id}`);
      await addDoc(collection(db,"documents"), {
        userId:      selectedMember.id,
        memberName:  selectedMember.name,
        memberId:    selectedMember.memberId,
        type:        "certificate",
        title:       form.title,
        eventName:   form.eventName,
        issuedDate:  form.issuedDate || new Date().toISOString().split("T")[0],
        url,
        createdAt:   new Date().toISOString(),
      });
      toast.success(`Certificate uploaded for ${selectedMember.name}!`);
      setFile(null); setPreview(null);
      setForm({ title:"", eventName:"", issuedDate:"" });
      setSelectedMember(null);
      if (fileRef.current) fileRef.current.value = "";
      loadData();
    } catch(e) {
      console.error(e);
      toast.error("Upload failed: " + e.message);
    } finally { setUploading(false); }
  }

  async function handleDelete(certId, memberName) {
    if (!window.confirm(`Delete this certificate for ${memberName}?`)) return;
    try {
      await deleteDoc(doc(db,"documents",certId));
      setCertificates(p=>p.filter(c=>c.id!==certId));
      toast.success("Certificate deleted");
    } catch(e) { toast.error("Failed to delete"); }
  }

  const filteredMembers = members.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.memberId?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredCerts = filterMember === "all"
    ? certificates
    : certificates.filter(c => c.userId === filterMember);

  return (
    <div>
      <Toaster position="top-center"/>

      {/* Header */}
      <div style={s.header}>
        <Award size={22} color="#f59e0b"/>
        <div>
          <h2 style={s.title}>Certificate Manager</h2>
          <p style={s.sub}>Upload participation certificates for members — they'll see it instantly in their Documents</p>
        </div>
      </div>

      <div style={s.grid2}>
        {/* ── Upload Form ── */}
        <div style={s.card}>
          <div style={s.cardTitle}><Upload size={15} color="#f59e0b"/> Upload Certificate</div>

          {/* Member Search */}
          <div style={{ marginBottom:14 }}>
            <label style={s.label}>Select Member *</label>
            {selectedMember ? (
              <div style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:10, padding:"10px 14px" }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:"rgba(245,158,11,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:"#f59e0b", flexShrink:0 }}>
                  {(selectedMember.name||"?").charAt(0).toUpperCase()}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#fef3e2" }}>{selectedMember.name}</div>
                  <div style={{ fontSize:11, color:"#78716c" }}>{selectedMember.memberId} · {selectedMember.role}</div>
                </div>
                <button onClick={() => setSelectedMember(null)} style={{ background:"none", border:"none", cursor:"pointer", color:"#78716c" }}>
                  <X size={16}/>
                </button>
              </div>
            ) : (
              <div>
                <div style={{ position:"relative", marginBottom:8 }}>
                  <Search size={13} color="#52504d" style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)" }}/>
                  <input value={search} onChange={e=>setSearch(e.target.value)}
                    placeholder="Search member by name or ID..."
                    style={{ ...s.input, paddingLeft:30 }}/>
                </div>
                {search && (
                  <div style={{ background:"#0a0908", border:"1px solid rgba(255,255,255,0.1)", borderRadius:9, maxHeight:180, overflowY:"auto" }}>
                    {filteredMembers.length === 0 ? (
                      <div style={{ padding:"12px 14px", fontSize:12, color:"#52504d" }}>No members found</div>
                    ) : filteredMembers.map(m => (
                      <div key={m.id} onClick={() => { setSelectedMember(m); setSearch(""); }}
                        style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", cursor:"pointer", borderBottom:"1px solid rgba(255,255,255,0.04)" }}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div style={{ width:28, height:28, borderRadius:"50%", background:"rgba(245,158,11,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:"#f59e0b" }}>
                          {(m.name||"?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize:13, fontWeight:600, color:"#fef3e2" }}>{m.name}</div>
                          <div style={{ fontSize:11, color:"#52504d" }}>{m.memberId} · {m.role}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Certificate Details */}
          <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:14 }}>
            <div>
              <label style={s.label}>Certificate Title *</label>
              <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}
                placeholder="e.g. Participation Certificate — Food Drive 2026"
                style={s.input}/>
            </div>
            <div>
              <label style={s.label}>Event Name</label>
              <input value={form.eventName} onChange={e=>setForm(p=>({...p,eventName:e.target.value}))}
                placeholder="e.g. Food Drive May 2026"
                style={s.input}/>
            </div>
            <div>
              <label style={s.label}>Issue Date</label>
              <input type="date" value={form.issuedDate} onChange={e=>setForm(p=>({...p,issuedDate:e.target.value}))}
                style={s.input}/>
            </div>
          </div>

          {/* File Upload */}
          <div onClick={() => fileRef.current?.click()}
            style={{ border:`2px dashed ${file?"#f59e0b":"rgba(255,255,255,0.1)"}`, borderRadius:10, cursor:"pointer", overflow:"hidden", background:file?"rgba(245,158,11,0.04)":"rgba(255,255,255,0.02)", transition:"all 0.2s", marginBottom:14 }}>
            {preview ? (
              <div style={{ position:"relative" }}>
                <img src={preview} alt="preview" style={{ width:"100%", maxHeight:160, objectFit:"cover" }}/>
                <button type="button" onClick={e=>{e.stopPropagation();setFile(null);setPreview(null);if(fileRef.current)fileRef.current.value="";}}
                  style={{ position:"absolute",top:8,right:8,background:"rgba(0,0,0,0.7)",border:"none",borderRadius:"50%",width:26,height:26,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#fff" }}>
                  <X size={13}/>
                </button>
              </div>
            ) : file ? (
              <div style={{ padding:"16px", display:"flex", alignItems:"center", gap:10 }}>
                <FileText size={24} color="#f59e0b"/>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:"#fef3e2" }}>{file.name}</div>
                  <div style={{ fontSize:11, color:"#78716c" }}>{Math.round(file.size/1024)}KB</div>
                </div>
                <button type="button" onClick={e=>{e.stopPropagation();setFile(null);if(fileRef.current)fileRef.current.value="";}}
                  style={{ marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#78716c" }}>
                  <X size={15}/>
                </button>
              </div>
            ) : (
              <div style={{ textAlign:"center", padding:"24px 16px" }}>
                <Upload size={28} color="#52504d" style={{ marginBottom:8 }}/>
                <div style={{ fontSize:13, fontWeight:600, color:"#78716c", marginBottom:4 }}>Click to upload certificate</div>
                <div style={{ fontSize:11, color:"#3a3835" }}>Image or PDF · Max 5MB</div>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={handleFile} style={{ display:"none" }}/>

          <button onClick={handleUpload} disabled={uploading} style={{ ...s.uploadBtn, opacity:uploading?0.6:1 }}>
            <Award size={16}/> {uploading ? "Uploading..." : "Upload & Issue Certificate"}
          </button>
        </div>

        {/* ── Issued Certificates ── */}
        <div style={s.card}>
          <div style={{ ...s.cardTitle, marginBottom:10 }}>
            <FileText size={15} color="#f59e0b"/> Issued Certificates
            <span style={s.countBadge}>{certificates.length} total</span>
          </div>

          {/* Filter by member */}
          <select value={filterMember} onChange={e=>setFilterMember(e.target.value)} style={{ ...s.input, marginBottom:12 }}>
            <option value="all">All Members ({certificates.length})</option>
            {members.map(m => (
              <option key={m.id} value={m.id}>
                {m.name} ({certificates.filter(c=>c.userId===m.id).length})
              </option>
            ))}
          </select>

          {loading ? (
            <div style={s.empty}>Loading...</div>
          ) : filteredCerts.length === 0 ? (
            <div style={{ textAlign:"center", padding:"32px 0" }}>
              <Award size={36} color="#1a1714" style={{ marginBottom:10 }}/>
              <div style={{ fontSize:13, color:"#52504d" }}>No certificates issued yet</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8, maxHeight:500, overflowY:"auto" }}>
              {filteredCerts.map(cert => (
                <div key={cert.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.05)", borderRadius:10 }}>
                  {cert.url && (
                    <img src={cert.url} alt={cert.title}
                      style={{ width:48, height:48, objectFit:"cover", borderRadius:8, flexShrink:0, border:"1px solid rgba(255,255,255,0.08)" }}
                      onError={e=>e.target.style.display="none"}/>
                  )}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#fef3e2", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {cert.title}
                    </div>
                    <div style={{ fontSize:11, color:"#f59e0b", fontWeight:600, marginTop:2 }}>
                      {cert.memberName}
                    </div>
                    <div style={{ fontSize:10, color:"#52504d", marginTop:1 }}>
                      {cert.eventName && `${cert.eventName} · `}{cert.issuedDate}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                    {cert.url && (
                      <a href={cert.url} target="_blank" rel="noreferrer"
                        style={{ width:28,height:28,borderRadius:7,background:"rgba(59,130,246,0.12)",border:"1px solid rgba(59,130,246,0.2)",color:"#3b82f6",display:"flex",alignItems:"center",justifyContent:"center",textDecoration:"none" }}>
                        <FileText size={13}/>
                      </a>
                    )}
                    <button onClick={() => handleDelete(cert.id, cert.memberName)}
                      style={{ width:28,height:28,borderRadius:7,background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.2)",color:"#ef4444",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  header:     { display:"flex", alignItems:"center", gap:14, marginBottom:20, background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 },
  title:      { fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:800, color:"#fef3e2", margin:"0 0 4px" },
  sub:        { fontSize:13, color:"#78716c", margin:0 },
  grid2:      { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, alignItems:"start" },
  card:       { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 },
  cardTitle:  { fontSize:13, fontWeight:700, color:"#fef3e2", marginBottom:16, display:"flex", alignItems:"center", gap:7 },
  countBadge: { marginLeft:"auto", fontSize:10, background:"rgba(245,158,11,0.15)", color:"#f59e0b", border:"1px solid rgba(245,158,11,0.25)", padding:"2px 8px", borderRadius:50 },
  label:      { display:"block", fontSize:11, fontWeight:700, color:"#78716c", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:5 },
  input:      { width:"100%", padding:"10px 12px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  uploadBtn:  { width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"13px", background:"#f59e0b", color:"#0a0908", border:"none", borderRadius:11, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit" },
  empty:      { fontSize:13, color:"#52504d", textAlign:"center", padding:"24px 0" },
};
